import TestsDataStorage from '@/Utils/TestsDataStorage.js';
import { useProjectStore } from '@/Stores/Web/projectStore';
import { useCurrencyStore } from '@/Stores/Web/currencyStore';
import { useProjectContextStore } from '@/Stores/Web/projectContextStore';
import { getAppPlatformCookie } from '@/Utils/PwaUtility';
import Config from '@/Config';
import analytics from '@/Analytics';
import { analyticsPluginName } from '@/Utils/AnalyticsUtility';

const analyticsPlatformPerPlatform = {
    'gamefound': 'gamefound',
    'indiegogo': 'indiegogo',
};

const analyticsPlatformAdsPerPlatform = {
    'gamefound': 'gamefound-ads',
    'indiegogo': 'indiegogo-ads',
};

const platformNameLowercase = Config.platformName.toLowerCase();

// Facebook Pixel Standard Events https://www.facebook.com/business/help/402791146561655
// Default Google Analytics Events  https://developers.google.com/analytics/devguides/collection/gtagjs/events#default_google_analytics_events - technically this for gtag.js but it's basically the same thing and we might upgrade someday

export const googleAnalyticsGroups = {
    platform: analyticsPlatformPerPlatform[platformNameLowercase], // Global analiticys group send to platform trackers (UA, GA4)
    creator: 'creator', // Creator group send to creator trackers (UA, GA4)
    platformAds: analyticsPlatformAdsPerPlatform[platformNameLowercase], // Globals google ads
};

export default class AnalyticsTracking {
    constructor() {
        const projectStore = useProjectStore();
        const projectContextStore = useProjectContextStore();
        this.analyticsConfig = projectStore.analyticsConfig;
        this.projectTrackingModel = projectStore.projectTrackingModel || {};
        this.isTestMode = projectContextStore.projectContext.isInTestMode;

        const platformCookie = getAppPlatformCookie();
        this.platformData = {
            'platform_name': platformCookie.platformName,
            'platform_type': platformCookie.platformType,
        };
    }

    sendCustomEvent({
        category,
        action,
        label,
        nonInteraction = false,
        value = null,
        sendTo = [googleAnalyticsGroups.platform, googleAnalyticsGroups.creator],
    }) {
        if (this.isTestMode || !this.analyticsConfig.googleAnalytics) {
            return;
        }

        if (!category || !action) {
            throw 'Category and action arguments are required.';
        }

        analytics.trackTo([analyticsPluginName.googleAnalytics], action, {
            'send_to': sendTo,
            'event_category': category,
            'event_label': label,
            value,
            'non_interaction': nonInteraction,
            ...this.platformData,
        });

        TestsDataStorage.setItem('customEvent', {
            category,
            action,
            label,
            nonInteraction,
            value,
            ...this.platformData,
        });
    }

    addToCartTracking(orderItem) {
        if (this.isTestMode) return;

        const { googleAds } = this.projectTrackingModel;
        const currencyStore = useCurrencyStore();
        const effectiveCurrency = currencyStore.effectiveCurrency;

        if (this.analyticsConfig.facebookPixel) {
            analytics.trackTo([analyticsPluginName.facebookPixel], 'AddToCart', {
                content_name: `[${ this.analyticsConfig.project.name }] ${ orderItem.displayName }`,
                content_type: 'product',
                value: orderItem.quantity * orderItem.priceConverted,
                currency: effectiveCurrency.shortName,
                contents: [
                    {
                        id: `${ orderItem.productID }`,
                        quantity: orderItem.quantity,
                        item_price: orderItem.priceConverted,
                    },
                ],
            });
        }

        if (this.analyticsConfig.googleAnalytics) {
            analytics.trackTo([analyticsPluginName.googleAnalytics], 'add_to_cart', {
                items: [
                    {
                        id: orderItem.productID,
                        name: `[${ this.analyticsConfig.project.name }] ${ orderItem.displayName }`,
                        variant: orderItem.options.map(o => o.valueText).join(', '),
                        quantity: orderItem.quantity,
                        price: orderItem.priceConverted,
                    },
                ],
                ...this.platformData,
                event_label: this.analyticsConfig.project.urlName,
                send_to: [googleAnalyticsGroups.platform, googleAnalyticsGroups.creator],
            });

            if (googleAds?.enabled && !!googleAds?.addToCartEventID) {
                analytics.trackTo([analyticsPluginName.googleAnalytics], 'conversion', {
                    send_to: [googleAds.addToCartEventID, googleAnalyticsGroups.platformAds],
                    value: orderItem.priceConverted,
                    currency: effectiveCurrency.shortName,
                    ...this.platformData,
                });
            }
        }

        TestsDataStorage.setItem('addToCart', orderItem);
    }

    registrationCompletedTracking({ email, value, currency }) {
        if (this.isTestMode) return;

        if (this.analyticsConfig.googleAnalytics) {
            analytics.trackTo([analyticsPluginName.googleAnalytics], 'sign_up', {
                send_to: [googleAnalyticsGroups.platform],
                ...this.platformData,
            });
        }

        if (this.analyticsConfig.facebookPixel) {
            analytics.trackTo([analyticsPluginName.facebookPixel], 'CompleteRegistration', {
                // TODO: This is against our privacy policy
                content_name: email,
                value,
                currency,
            });
        }

        TestsDataStorage.setItem('registrationCompleted', { email, value, currency, ...this.platformData });
    }

    followTracking({ projectName, source = 'Button' }) {
        if (this.isTestMode) return;

        const { googleAds } = this.projectTrackingModel;

        this.sendCustomEvent({
            category: 'project',
            action: 'follow',
            label: `${ source }:${ projectName }`,
        });

        if (this.analyticsConfig.facebookPixel) {
            analytics.trackTo([analyticsPluginName.facebookPixel], 'follow', {
                category: 'project',
                label: `${ source }:${ projectName }`,
            });
        }

        if (this.analyticsConfig.googleAnalytics && googleAds?.enabled && !!googleAds?.followEventID) {
            analytics.trackTo([analyticsPluginName.googleAnalytics], 'conversion', {
                send_to: [googleAds.followEventID, googleAnalyticsGroups.platformAds],
                ...this.platformData,
            });
        }

        TestsDataStorage.setItem('follow', { projectName, ...this.platformData });
    }

    unfollowTracking({ projectName, source = 'Button' }) {
        if (this.isTestMode) return;

        this.sendCustomEvent({
            category: 'project',
            action: 'unfollow',
            label: `${ source }:${ projectName }`,
        });

        if (this.analyticsConfig.facebookPixel) {
            analytics.trackTo([analyticsPluginName.facebookPixel], 'unfollow', {
                category: 'project',
                label: `${ source }:${ projectName }`,
            });
        }

        TestsDataStorage.setItem('unfollow', { projectName });
    }

    purchaseTracking(transaction) {
        if (this.isTestMode) return;

        const { googleAds } = this.projectTrackingModel;
        const orderLocalKey = `${Config.platformName}.Tracking.OrderCode`;
        const orderLocalCode = sessionStorage.getItem(orderLocalKey);

        if (this.analyticsConfig.googleAnalytics && orderLocalCode !== transaction.transactionId) {
            analytics.trackTo([analyticsPluginName.googleAnalytics], 'purchase', {
                event_label: transaction.projectUrlName,
                transaction_id: transaction.transactionId,
                affiliation: transaction.affiliation,
                value: transaction.revenue,
                currency: transaction.currency,
                shipping: transaction.shipping,
                items: transaction.items,
                metric1: transaction.metric1,
                send_to: [googleAnalyticsGroups.platform, googleAnalyticsGroups.creator],
                ...this.platformData,
            });

            if (googleAds?.enabled && !!googleAds?.purchaseEventID) {
                analytics.trackTo([analyticsPluginName.googleAnalytics], 'conversion', {
                    send_to: [googleAds.purchaseEventID, googleAnalyticsGroups.platformAds],
                    value: transaction.revenue,
                    currency: transaction.currency,
                    transaction_id: transaction.transactionId,
                    ...this.platformData,
                });
            }

            sessionStorage.setItem(orderLocalKey, transaction.transactionId);
        }

        if (this.analyticsConfig.facebookPixel) {
            analytics.trackTo([analyticsPluginName.facebookPixel], 'Purchase', {
                content_type: 'product',
                value: transaction.revenue,
                currency: transaction.currency,
                contents: transaction.items.map(item => ({
                    id: item.productId,
                    item_price: item.price,
                    quantity: item.quantity,
                })),
            });
        }

        TestsDataStorage.setItem('purchase', transaction);
    }
}
